import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { AxiosPublico } from "../../../componentes/axios/Axios";
import Card from '../../../componentes/contenedores/Card';
import GraficoBarra from '../../../componentes/graficos/Barras';
import GraficoCircular from '../../../componentes/graficos/Circular';
import FiltroFechaUI from "../../../componentes/metodos/FiltroFechaUI";
import useFechaFiltro from "../../../componentes/metodos/LogicaFiltro";
import Page from '../../../componentes/plantilla/Page';
import {
    AnalisisEvolucionCompras,
    AnalisisMetodosPagosCompras,
    AnalisisMoraCompras,
    AnalisisResumenKPICompras
} from '../../../configuracion/apiUrls';
import { formatoMoneda } from "../../../configuracion/validaciones";
moment.locale("es");

const ComprasAnalisis = () => {
    const [cargando, setCargando] = useState(true);
    const [metricas, setMetricas] = useState(null);
    const [chartPagos, setChartPagos] = useState(null);
    const [chartEvolucion, setChartEvolucion] = useState(null);
    const [datosMora, setDatosMora] = useState(null);

    const {
        filtro, setFiltro, fechaSeleccionada, setFechaSeleccionada,
        horaSeleccionada, setHoraSeleccionada, semanaSeleccionada, setSemanaSeleccionada,
        mesSeleccionado, setMesSeleccionado, anioSeleccionado, setAnioSeleccionado,
        obtenerFechasDeFiltro, maxAnio,
    } = useFechaFiltro();

    const cargarTodo = useCallback(async () => {
        setCargando(true);
        const fechas = obtenerFechasDeFiltro();
        const params = {
            fechaInicio: moment(fechas.fechaInicio).format('YYYY-MM-DD HH:mm:ss'),
            fechaFin: moment(fechas.fechaFin).format('YYYY-MM-DD HH:mm:ss'),
        };
        try {
            const [resKPI, resPagos, resEvol, resMora] = await Promise.all([
                AxiosPublico.get(AnalisisResumenKPICompras, { params }),
                AxiosPublico.get(AnalisisMetodosPagosCompras, { params }),
                AxiosPublico.get(AnalisisEvolucionCompras, { params }),
                AxiosPublico.get(AnalisisMoraCompras, { params })
            ]);

            setMetricas(resKPI.data);
            setDatosMora(resMora.data);

            // 1. Gráfico Circular: Métodos de Pago (Contado)
            if (resPagos.data?.length > 0) {
                const totalAbsoluto = resPagos.data.reduce((acc, p) => acc + parseFloat(p.total), 0);
                setChartPagos({
                    labels: resPagos.data.map(p => p.metodo),
                    data: resPagos.data.map(p => {
                        const porcentaje = (parseFloat(p.total) / totalAbsoluto) * 100;
                        return parseFloat(porcentaje.toFixed(2)); // Limitar a 2 decimales
                    }),
                    colors: ['#1cc88a', '#4e73df', '#36b9cc'], // Verde, Azul, Cian
                    titulo: "Distribución de Pagos (Contado)"
                });
            }

            // 2. Gráfico Comparativo: Contado (Verde) vs Crédito (Azul)
            if (resEvol.data?.length > 0) {
                setChartEvolucion({
                    labels: resEvol.data.map(d => moment(d.fecha).format("DD/MM")),
                    datasets: [
                        {
                            label: "Contado",
                            data: resEvol.data.map(d => parseFloat(d.montoContado || 0)),
                            backgroundColor: '#1cc88a',
                        },
                        {
                            label: "Crédito",
                            data: resEvol.data.map(d => parseFloat(d.montoCredito || 0)),
                            backgroundColor: '#4e73df',
                        }
                    ],
                    titulo: "Evolución Diaria: Contado vs Crédito"
                });
            }

        } catch (error) {
            console.error("Error al cargar análisis de compras:", error);
        } finally {
            setCargando(false);
        }
    }, [obtenerFechasDeFiltro]);

    useEffect(() => { cargarTodo(); }, []);

    const datos = {
        titulo: {
            titulo: "Análisis de Compras",
            url: "/app/admin/analisis/compras",
            tituloUrl: "Compras",
            nombreUrl: "Gestión de Abastecimiento",
        },
    };

    return (
        <Page datos={datos}>
            <Card>
                <FiltroFechaUI
                    filtro={filtro} setFiltro={setFiltro}
                    fechaSeleccionada={fechaSeleccionada} setFechaSeleccionada={setFechaSeleccionada}
                    horaSeleccionada={horaSeleccionada} setHoraSeleccionada={setHoraSeleccionada}
                    semanaSeleccionada={semanaSeleccionada} setSemanaSeleccionada={setSemanaSeleccionada}
                    mesSeleccionado={mesSeleccionado} setMesSeleccionado={setMesSeleccionado}
                    anioSeleccionado={anioSeleccionado} setAnioSeleccionado={setAnioSeleccionado}
                    handleApplyFilter={cargarTodo}
                    maxAnio={maxAnio}
                />
                <hr />
                {cargando ? (
                    <div className="text-center p-5">
                        <div className="spinner-border text-primary" role="status"></div>
                        <p className="mt-2">Cargando indicadores...</p>
                    </div>
                ) : (
                    <>
                        {/* 1. Tarjetas de KPIs */}
                        <div className="row mb-4">
                            <div className="col-lg-3 col-md-6 mb-3">
                                <div className="p-3 border-start border-primary border-4 bg-light rounded shadow-sm h-100">
                                    <h6 className="text-muted small text-uppercase fw-bold">Total Compras Netas</h6>
                                    <h3 className="fw-bold mb-0">{formatoMoneda(metricas?.totalNeto || 0).toLocaleString()}</h3>
                                </div>
                            </div>
                            <div className="col-lg-3 col-md-6 mb-3">
                                <div className="p-3 border-start border-success border-4 bg-light rounded shadow-sm h-100">
                                    <h6 className="text-muted small text-uppercase fw-bold">Contado</h6>
                                    <div className="mt-2">
                                        <div className="d-flex justify-content-between align-items-end mb-1">
                                            <h3 className="fw-bold mb-0">{metricas?.porcentajeContado || 0}%</h3>
                                            <span className="text-secondary fw-medium small">{formatoMoneda(metricas?.montoContado || 0)}</span>
                                        </div>
                                        <div className="progress" style={{ height: "6px" }}>
                                            <div
                                                className="progress-bar bg-success"
                                                style={{ width: `${metricas?.porcentajeContado || 0}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-3 col-md-6 mb-3">
                                <div className="p-3 border-start border-info border-4 bg-light rounded shadow-sm h-100">
                                    <h6 className="text-muted small text-uppercase fw-bold">Crédito</h6>
                                    <div className="mt-2">
                                        <div className="d-flex justify-content-between align-items-end mb-1">
                                            <h3 className="fw-bold mb-0">{metricas?.porcentajeCredito || 0}%</h3>
                                            <span className="text-secondary fw-medium small">{formatoMoneda(metricas?.montoCredito || 0)}</span>
                                        </div>
                                        <div className="progress" style={{ height: "6px" }}>
                                            <div
                                                className="progress-bar bg-info"
                                                style={{ width: `${metricas?.porcentajeCredito || 0}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-3 col-md-6 mb-3">
                                <div className="p-3 border-start border-danger border-4 bg-light rounded shadow-sm h-100">
                                    <h6 className="text-muted small text-uppercase fw-bold">Tasa de Mora</h6>
                                    <h3 className="fw-bold mb-0 text-danger">{metricas?.tasaMora || 0}%</h3>
                                </div>
                            </div>
                        </div>

                        {/* 2. Gráficos */}
                        <div className="row mb-5">
                            {/* Gráfico Circular */}
                            <div className="col-lg-4 mb-4">
                                <div className="p-3 bg-white shadow-sm rounded border h-100">
                                    {chartPagos ? (
                                        <GraficoCircular datos={chartPagos} esMonetario={true} />
                                    ) : (
                                        <p className="text-center text-muted mt-5">Sin datos de pagos</p>
                                    )}
                                </div>
                            </div>
                            {/* Gráfico de Barras Comparativo */}
                            <div className="col-lg-8 mb-4">
                                <div className="p-3 bg-white shadow-sm rounded border h-100">
                                    {chartEvolucion ? (
                                        <GraficoBarra datos={chartEvolucion} esMonetario={true} />
                                    ) : (
                                        <p className="text-center text-muted mt-5">Sin datos de evolución</p>
                                    )}
                                </div>
                            </div>
                        </div>

                            <div className="row mt-4">
                                <div className="col-lg-4 mb-4">
                                    <div className="card border-0 shadow-sm h-100" style={{ backgroundColor: '#fdf2f2', border: '1px solid #f8d7da' }}>
                                        <div className="card-body text-center d-flex flex-column justify-content-center border-start border-danger border-5 rounded">
                                            <h6 className="text-danger fw-bold text-uppercase small">Resumen de Mora (Proveedores)</h6>
                                            <h1 className="display-5 fw-bold text-dark mt-2">{formatoMoneda(datosMora?.totalVencido || 0)}</h1>
                                            <p className="text-muted mb-3">Deuda Vencida Total 🔴</p>
                                            <hr />
                                            <h5 className="fw-bold text-dark">
                                                {datosMora?.proveedoresEnMora || 0} <span className="fw-normal small text-muted">Proveedores con saldos pendientes</span>
                                            </h5>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-lg-8 mb-4">
                                    <div className="card shadow-sm border-0 h-100">
                                        <div className="card-header bg-white py-3">
                                            <h6 className="m-0 fw-bold text-primary">Top Proveedores Vencidos</h6>
                                        </div>
                                        <div className="card-body p-0">
                                            <div className="table-responsive">
                                                <table className="table table-hover align-middle mb-0">
                                                    <thead className="bg-light">
                                                        <tr>
                                                            <th className="px-4">Proveedor</th>
                                                            <th>Monto Vencido</th>
                                                            <th>Días Atraso</th>
                                                            <th className="text-center">Estado</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {datosMora?.detalle?.length > 0 ? (
                                                            datosMora.detalle.slice(0, 5).map((item, index) => {
                                                                let colorTexto = "text-warning";
                                                                let estiloColor = {};
                                                                if (item.diasMora > 90) colorTexto = "text-danger";
                                                                else if (item.diasMora >= 60) { estiloColor = { color: '#fd7e14' }; colorTexto = ""; }

                                                                return (
                                                                    <tr key={index}>
                                                                        <td className="px-4 fw-medium text-dark">{item.proveedorNombre || 'Sin nombre'}</td>
                                                                        <td className="fw-bold">{formatoMoneda(item.saldo)}</td>
                                                                        <td>
                                                                            <span className={`fw-bold ${colorTexto}`} style={estiloColor}>
                                                                                {`${item.diasMora} Días`}
                                                                            </span>
                                                                        </td>
                                                                        <td className="text-center">
                                                                            <span className={`badge rounded-pill ${item.estado === 'Mora' ? 'bg-danger' : 'bg-warning text-dark'}`}>
                                                                                {item.estado}
                                                                            </span>
                                                                        </td>
                                                                    </tr>
                                                                );
                                                            })
                                                        ) : (
                                                            <tr><td colSpan="4" className="text-center py-4 text-muted">No hay deudas vencidas</td></tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                    </>
                )}
            </Card>
        </Page>
    );
};

export default ComprasAnalisis;